import styled from '@emotion/styled'
import { css, keyframes } from '@emotion/react'

import type { GuideTourStepCardSize } from '../types'
import { GUIDE_TOUR_STEP_CARD_WIDTH_BY_SIZE } from './stepCardSize'
import type { GuideTourStepCardArrowSide } from './utils'

const cardInnerPadding: Record<GuideTourStepCardSize, string> = {
  small: '24px',
  middle: '28px',
  large: '32px',
}

const cardInnerGap: Record<GuideTourStepCardSize, string> = {
  small: '16px',
  middle: '18px',
  large: '20px',
}

const titleFontSize: Record<GuideTourStepCardSize, string> = {
  small: '20px',
  middle: '22px',
  large: '24px',
}

const bodyFontSize: Record<GuideTourStepCardSize, string> = {
  small: '14px',
  middle: '15px',
  large: '16px',
}

const stepLabelFontSize: Record<GuideTourStepCardSize, string> = {
  small: '14px',
  middle: '15px',
  large: '16px',
}

/** Card palette — change CARD_BG / ACCENT_YELLOW once; surfaces follow via color-mix */
const CARD_BG = '#1e1e24'
const ACCENT_YELLOW = '#ffcc00'
const TEXT_HIGH = '#ffffff'
const TEXT_BODY = '#ffffff'

const CARD_SURFACE_RAISED = `color-mix(in srgb, ${CARD_BG}, white 26%)`
const ACCENT_SOFT_BG = `color-mix(in srgb, ${ACCENT_YELLOW} 14%, ${CARD_BG})`
const ACCENT_SOFT_BORDER = `color-mix(in srgb, ${ACCENT_YELLOW} 50%, ${CARD_BG})`
const ACCENT_SOFT_TEXT = `color-mix(in srgb, ${ACCENT_YELLOW} 30%, ${TEXT_HIGH})`
const ACCENT_BADGE_BG = `color-mix(in srgb, ${ACCENT_YELLOW} 16%, transparent)`
const PRIMARY_BUTTON_TEXT = `color-mix(in srgb, ${CARD_BG}, black 35%)`

const primaryButtonPulse = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, ${ACCENT_YELLOW} 0%, transparent);
  }

  40% {
    box-shadow: 0 0 0 4px color-mix(in srgb, ${ACCENT_YELLOW} 45%, transparent);
  }
`

const arrowBase = css`
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
`

const arrowPlacementBySide: Record<
  GuideTourStepCardArrowSide,
  ReturnType<typeof css>
> = {
  left: css`
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 8px 8px 8px 0;
    border-color: transparent ${CARD_BG} transparent transparent;
  `,
  right: css`
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    border-width: 8px 0 8px 8px;
    border-color: transparent transparent transparent ${CARD_BG};
  `,
  top: css`
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 0 8px 8px;
    border-color: transparent transparent ${CARD_BG};
  `,
  bottom: css`
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px 8px 0;
    border-color: ${CARD_BG} transparent transparent;
  `,
}

const arrowCss = (side: GuideTourStepCardArrowSide) => css`
  ${arrowBase}
  ${arrowPlacementBySide[side]}
`

export const Card = styled.div<{
  elevated?: boolean
  $arrowSide?: GuideTourStepCardArrowSide | null
  $cardSize?: GuideTourStepCardSize
}>`
  position: fixed;
  z-index: ${({ elevated }) => (elevated ? 10051 : 10001)};
  width: min(
    ${({ $cardSize = 'small' }) =>
      GUIDE_TOUR_STEP_CARD_WIDTH_BY_SIZE[$cardSize]}px,
    calc(100vw - 32px)
  );
  background: ${CARD_BG};
  border-radius: 8px;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
  overflow: visible;

  ${({ $arrowSide }) =>
    $arrowSide
      ? css`
          &::before {
            ${arrowCss($arrowSide)}
          }
        `
      : ''}
`

export const CardInner = styled.div<{ $cardSize?: GuideTourStepCardSize }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $cardSize = 'small' }) => cardInnerGap[$cardSize]};
  padding: ${({ $cardSize = 'small' }) => cardInnerPadding[$cardSize]};
`

export const DismissRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin: -8px 0 -8px;
`

export const CompactStepMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

/** Used by `GuideTourStepCardHeader` (not mounted in the step card yet). */
export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/** Used by `GuideTourStepCardHeader` (not mounted in the step card yet). */
export const HeaderTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`

export const CloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -6px -6px 0 0;
  padding: 0;
  border: none;
  border-radius: 64px;
  background: ${CARD_SURFACE_RAISED};
  color: ${TEXT_HIGH};
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  font-weight: 200;

  &:focus-visible {
    outline: 2px solid ${ACCENT_YELLOW};
    outline-offset: 2px;
  }
`

export const BlockedInteractionDangerNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #f54337;
  border-radius: 8px;
  background: rgba(245, 67, 55, 0.12);
  color: #ffc9c5;
  font-size: 11px;
  font-weight: 200;
  line-height: 1.35;
`

export const SkippedStepInfoNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid ${ACCENT_SOFT_BORDER};
  border-radius: 8px;
  background: ${ACCENT_SOFT_BG};
  color: ${ACCENT_SOFT_TEXT};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
`

/** Used by `GuideTourStepCardHeader` (not mounted in the step card yet). */
export const RouteTourLabel = styled.span`
  color: ${ACCENT_YELLOW};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

/** Used by `GuideTourStepCardHeader` (not mounted in the step card yet). */
export const StepMetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

/** Used by `GuideTourStepCardHeader` (not mounted in the step card yet). */
export const StepLabel = styled.span<{ $cardSize?: GuideTourStepCardSize }>`
  color: ${ACCENT_YELLOW};
  font-size: ${({ $cardSize = 'small' }) => stepLabelFontSize[$cardSize]};
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
`

/** Used by `GuideTourStepCardHeader` (not mounted in the step card yet). */
export const Badge = styled.span`
  display: inline-block;
  max-width: 100%;
  background: ${ACCENT_BADGE_BG};
  color: ${ACCENT_YELLOW};
  font-size: 11px;
  font-weight: 500;
  line-height: 1.25;
  padding: 4px 8px;
  border-radius: 999px;
  margin-left: -7px;
  position: relative;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const Title = styled.h2<{ $cardSize?: GuideTourStepCardSize }>`
  margin: 0;
  color: ${TEXT_HIGH};
  font-family: Barlow, system-ui, sans-serif;
  font-size: ${({ $cardSize = 'small' }) => titleFontSize[$cardSize]};
  font-weight: 700;
  line-height: 1.3;
`

export const Body = styled.p<{ $cardSize?: GuideTourStepCardSize }>`
  margin: 0;
  color: ${TEXT_BODY};
  font-family: Barlow, system-ui, sans-serif;
  font-size: ${({ $cardSize = 'small' }) => bodyFontSize[$cardSize]};
  font-weight: 400;
  line-height: 20px;
`

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`

export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`

const actionButtonTypography = css`
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`

/** Tertiary action — same type scale as primary; txt/button/tertiary/default @ 88% */
const TERTIARY_BUTTON_TEXT = `color-mix(in srgb, ${TEXT_HIGH} 88%, transparent)`

export const SecondaryButton = styled.button`
  border: none;
  border-radius: 8px;
  background: #3d3d3d;
  color: ${TERTIARY_BUTTON_TEXT};
  ${actionButtonTypography}
  cursor: pointer;
  padding: 0 16px;
  height: 32px;

  &:focus-visible {
    outline: 2px solid ${ACCENT_YELLOW};
    outline-offset: 2px;
  }
`

export const PrimaryButton = styled.button<{ $pulseTrigger?: number }>`
  border: none;
  border-radius: 8px;
  background: ${ACCENT_YELLOW};
  color: ${PRIMARY_BUTTON_TEXT};
  ${actionButtonTypography}
  cursor: pointer;
  padding: 0 16px;
  height: 32px;
  min-width: 100px;
  animation: ${({ $pulseTrigger = 0 }) =>
    $pulseTrigger > 0
      ? css`
          ${primaryButtonPulse} 0.55s ease-out
        `
      : 'none'};

  &:focus-visible {
    outline: 2px solid ${TEXT_HIGH};
    outline-offset: 2px;
  }
`

const activationSpinnerSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const ActivationLoadingInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 0;
  text-align: center;
`

export const ActivationSpinner = styled.span`
  display: block;
  width: 32px;
  height: 32px;
  border: 3px solid color-mix(in srgb, ${ACCENT_YELLOW} 25%, transparent);
  border-top-color: ${ACCENT_YELLOW};
  border-radius: 50%;
  animation: ${activationSpinnerSpin} 0.75s linear infinite;
`

export const ActivationLoadingMessage = styled.p`
  margin: 0;
  color: ${TEXT_BODY};
  font-family: Barlow, system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`
